import {
  CANNOT_DELETE_ITEM_IN_USE_MSG,
  CloseButton,
  CrudMode,
  DELETE_WARNING,
  DropDownMenu,
  ListContext,
  ListContextValue,
  MenuItemType,
  ScreenType,
  Stack,
  Switch,
  Typography,
  UpdateButton,
  ValidationErrors,
  newToast,
} from "@compass/core";
import { DataModelInformation, SubmitButton } from "components";
import { College, DataModelEnum, Permission, PermissionAction } from "generated/openApi";
import { useContext, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { CollegeContext, CollegeContextValue } from "./CollegePage";

export const CollegePageHeader = (props: {
  rootPermission: Permission;
  handle: string;
  crudMode: CrudMode;
  fetch: (id: number) => void;
  reroute: (id: number, crudMode: CrudMode) => void;
  college: College;
  setCollege: (college: College) => void;
  initCollege: College;
  setValidationErrors: (errors: ValidationErrors) => void;
  unsavedChanges: boolean;
  resetUnsavedChanges: (items: any[]) => void;
  closeOnSave?: boolean | undefined;
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const collegeContext = useContext<CollegeContextValue>(CollegeContext);
  const listContext = useContext<ListContextValue>(ListContext);
  const id = props.college.id;
  const [createAnother, setCreateAnother] = useState<boolean>(false);

  const save = (college: College) => {
    props.setValidationErrors({});

    if (id > 0) {
      ApiService.colleges
        .saveUpdate(college)
        .then(() => {
          props.fetch(id);
          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
          if (props.closeOnSave) navigate("../");
          else props.reroute(id, "view");
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          props.setValidationErrors(ex.data);
        });
    } else {
      ApiService.colleges
        .saveCreate(college)
        .then((response) => {
          const id = response;
          if (createAnother) {
            props.setCollege(props.initCollege);
            props.resetUnsavedChanges([props.initCollege]);
          } else {
            props.fetch(id);
            if (props.closeOnSave) navigate("../");
            else props.reroute(id, "view");
          }

          collegeContext.onCreate?.(id);
          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          props.setValidationErrors(ex.data);
        });
    }
  };

  const postDelete = (id: number) => {
    if (window.confirm(DELETE_WARNING)) {
      ApiService.colleges.delete(id).then(() => {
        navigate("../");
        listContext.loadList?.();
        dispatch(setAppToast(newToast("success")));
      });
    }
  };

  const menuItems: MenuItemType[] = [];
  if (id > 0) {
    if (isAuthorized(props.rootPermission, PermissionAction.Delete))
      menuItems.push({
        label: <Typography color="error">Delete</Typography>,
        action: () => postDelete(id),
        disabled: props.college.isInUse,
        tooltip: props.college.isInUse ? CANNOT_DELETE_ITEM_IN_USE_MSG : undefined,
      });
  }

  return (
    <Stack direction="row" justifyContent="space-between">
      <Stack direction="row" alignItems="center" spacing="sm">
        <Typography variant="inherit">
          {id === 0 ? "New" : ""} {props.handle}
        </Typography>
        {menuItems.length > 0 && <DropDownMenu menuItems={menuItems} />}
      </Stack>

      <Stack direction="row" alignItems="center" spacing="sm">
        {id > 0 && <DataModelInformation dataModelType={DataModelEnum.College} dataModelId={id} />}

        {id === 0 && (
          <Switch value={createAnother} onChange={(e) => setCreateAnother(e.target.checked)} label="+ more" />
        )}

        <CloseButton theme="Dark" confirm={props.unsavedChanges} />

        {props.crudMode === "view" && isAuthorized(props.rootPermission, PermissionAction.Update) && (
          <UpdateButton theme="Dark" onClick={() => props.reroute(id, "edit")} />
        )}

        {props.crudMode === "edit" && (
          <SubmitButton theme="Dark" onClick={() => save(props.college)} disabled={!props.unsavedChanges} />
        )}
      </Stack>
    </Stack>
  );
};
